import React, { useEffect, useRef, useState } from 'react';
import Icon from './Icons';
import SmsConsent from './SmsConsentCheckbox';
import { formatPhone, phoneDigits, consentRecord } from '../../lib/submit';

const EMPTY = { fullName: '', companyName: '', email: '', phoneNumber: '', message: '' };
const NO_CONSENT = { smsCustomerCare: false, smsMarketing: false };

/**
 * Shared contact and SMS consent form. Actual server-side record is required
 * before showing success. Never display a success message on a failed request.
 */
export default function ContactRequestForm({ mode = 'contact' }) {
  const smsPage = mode === 'sms-opt-in';
  const confirmationRef = useRef(null);
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [form, setForm] = useState(EMPTY);
  const [consent, setConsent] = useState(NO_CONSENT);
  const [validation, setValidation] = useState('');
  const wantsSms = consent.smsCustomerCare || consent.smsMarketing;
  const set = (key) => (event) => {
    setValidation('');
    setForm((current) => ({ ...current, [key]: key === 'phoneNumber' ? formatPhone(event.target.value) : event.target.value }));
  };
  const setConsentField = (key, selected) => {
    setValidation('');
    setConsent((current) => ({ ...current, [key]: selected }));
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (sending) return;
    setValidation('');
    if (wantsSms && phoneDigits(form.phoneNumber).length !== 10) {
      setValidation('Enter a valid 10-digit US mobile number for SMS updates, or uncheck the SMS boxes.');
      return;
    }
    setSending(true);
    try {
      const response = await fetch('/api/submit.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          form_source: smsPage ? 'sms-opt-in' : 'contact',
          website_url: websiteUrl,
          sms_customer_care: consent.smsCustomerCare ? 'yes' : 'no',
          sms_marketing: consent.smsMarketing ? 'yes' : 'no',
          ...consentRecord(consent),
        }),
      });
      const result = await response.json().catch(() => null);
      if (!response.ok || !result || result.ok !== true) {
        throw new Error(result?.message || 'We could not record your request. Please try again or email info@bitnexfreight.com.');
      }
      setSubmitted(true);
    } catch (error) {
      setValidation(error.message || 'We could not submit your request. Please email info@bitnexfreight.com.');
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    if (!submitted || !confirmationRef.current) return undefined;
    // Reveal is managed by the parent page only on its initial mount. A new
    // `.reveal` panel inserted here was stuck at opacity: 0, appearing blank.
    // Focus and scroll the visible confirmation into view after the form shrinks.
    const frame = window.requestAnimationFrame(() => {
      const panel = confirmationRef.current;
      if (!panel) return;
      panel.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
      const heading = panel.querySelector('[data-confirmation-heading]');
      if (heading) heading.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [submitted]);

  const reset = () => {
    setForm(EMPTY);
    setConsent(NO_CONSENT);
    setValidation('');
    setSubmitted(false);
    setWebsiteUrl('');
  };

  if (submitted) {
    return (
      <div ref={confirmationRef} className="card scroll-mt-28 p-7 sm:p-9" role="status" aria-live="polite">
        <div className="eyebrow mb-2">{smsPage ? 'SMS consent form' : 'Send a message'}</div>
        <h2 className="h3">Every inquiry gets a reply.</h2>
        <div className="mt-8 rounded-xl border border-green-200 bg-green-50 p-6 text-green-900">
          <h3 data-confirmation-heading tabIndex={-1} className="flex items-center gap-2 font-display text-xl font-bold text-green-900 focus:outline-none">
            <Icon name="check" className="h-5 w-5" /> Thanks for reaching out!
          </h3>
          <p className="mt-2 text-[0.92rem]">
            Your request has been recorded. If you opted in to text messages, we have saved the categories you selected. SMS messages will begin only after our messaging program is approved and active.
          </p>
          <button type="button" onClick={reset} className="mt-4 inline-block text-[0.9rem] font-bold underline">Send another</button>
        </div>
      </div>
    );
  }

  return (
    <form
      className="card scroll-mt-28 p-7 sm:p-9"
      id={smsPage ? 'opt-in-form' : 'contact-form'}
      onSubmit={onSubmit}
    >
      <input type="text" name="website_url" value={websiteUrl} onChange={(e) => setWebsiteUrl(e.target.value)} autoComplete="off" tabIndex={-1} aria-hidden="true" className="absolute -left-[9999px] opacity-0" />
      <div className="eyebrow mb-2">{smsPage ? 'SMS consent form' : 'Send a message'}</div>
      <h2 className="h3">{smsPage ? 'Choose your SMS preferences.' : 'Every inquiry gets a reply.'}</h2>
      <p className="mt-2 text-[0.9rem] text-ink-600">
        {smsPage
          ? 'Complete the fields below and check the text-message types you want. Both boxes are unchecked by default.'
          : 'Fields marked * are required. SMS consent is optional and is not required to contact us or rent a trailer.'}
      </p>

      <div className="mt-8 grid gap-5 sm:grid-cols-2">
        <div>
          <label className="label" htmlFor={`${mode}-name`}>Full name *</label>
          <input id={`${mode}-name`} name="fullName" className="input" value={form.fullName} onChange={set('fullName')} required maxLength={120} autoComplete="name" placeholder="Jane Carrier" />
        </div>
        <div>
          <label className="label" htmlFor={`${mode}-company`}>Company / MC (optional)</label>
          <input id={`${mode}-company`} name="companyName" className="input" value={form.companyName} onChange={set('companyName')} maxLength={140} autoComplete="organization" placeholder="XYZ Trucking · MC 123456" />
        </div>
        <div>
          <label className="label" htmlFor={`${mode}-email`}>Email {smsPage ? '(optional)' : '*'}</label>
          <input id={`${mode}-email`} name="email" type="email" className="input" value={form.email} onChange={set('email')} required={!smsPage} maxLength={180} autoComplete="email" placeholder="you@company.com" />
        </div>
        <div>
          <label className="label" htmlFor={`${mode}-phone`}>{wantsSms ? 'Mobile phone number *' : 'Mobile phone number (optional)'}</label>
          <input id={`${mode}-phone`} name="phoneNumber" type="tel" inputMode="tel" className="input" value={form.phoneNumber} onChange={set('phoneNumber')} required={wantsSms} autoComplete="tel" placeholder="(555) 555-5555" />
        </div>
        <div className="sm:col-span-2">
          <label className="label" htmlFor={`${mode}-message`}>How can we help? {smsPage ? '(optional)' : '*'}</label>
          <textarea id={`${mode}-message`} name="message" className="input min-h-[140px]" value={form.message} onChange={set('message')} required={!smsPage} maxLength={2000} placeholder={smsPage ? 'Any questions about your SMS preferences? (optional)' : 'Trailer type, pickup date, lane, anything else…'} />
        </div>
        <div className="sm:col-span-2">
          <SmsConsent values={consent} onChange={setConsentField} idPrefix={smsPage ? 'optin' : 'contact'} />
        </div>
        {validation && <p role="alert" className="text-[0.9rem] font-semibold text-red-700 sm:col-span-2">{validation}</p>}
        <div className="sm:col-span-2">
          <button type="submit" disabled={sending} className="btn-primary w-full sm:w-auto disabled:opacity-60">
            {sending ? 'Submitting…' : 'Send message'} <Icon name="arrowRight" className="h-4 w-4" />
          </button>
        </div>
      </div>
    </form>
  );
}
